import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ticketService } from '../../api/ticketService';
import { usePriorities } from '../../hooks/usePriorities';
import { useCategories } from '../../hooks/useCategories';
import { extractErrorMessage } from '../../utils/errorHandler';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { 
  Ticket, 
  Search, 
  Filter, 
  Clock, 
  User, 
  MessageSquare, 
  CheckCircle, 
  AlertCircle, 
  Eye,
  Edit,
  RefreshCw,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Tag
} from 'lucide-react';

const AdminTicketsList = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortBy, setSortBy] = useState('updated');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [paginationData, setPaginationData] = useState(null);
  const { data: priorities = [] } = usePriorities();
  const { data: categories = [] } = useCategories();

  // Reset to page 0 when filters change
  useEffect(() => {
    if (currentPage !== 0) {
      setCurrentPage(0);
    }
  }, [statusFilter, priorityFilter, searchTerm, sortBy]);

  useEffect(() => {
    loadTickets();
  }, [currentPage, pageSize, statusFilter, priorityFilter, searchTerm, sortBy]);

  const breakAfter50 = (text) => {
    if (!text) return '';
    // Split text into chunks of 50 characters
    const chunks = [];
    for (let i = 0; i < text.length; i += 50) {
      chunks.push(text.slice(i, i + 50));
    }
    // Return array of React fragments with line breaks
    return chunks.map((chunk, index) => (
      <React.Fragment key={index}>
        {chunk}
        {index < chunks.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Build API params similar to AllUsers
      const params = {
        page: currentPage,
        size: pageSize
      };

      // Add search term if provided
      if (searchTerm) {
        params.search = searchTerm;
      }

      // Add status filter if not 'all'
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      // Add priority filter if not 'all'
      if (priorityFilter !== 'all') {
        params.priority = priorityFilter;
      }

      // Add sort parameter
      if (sortBy) {
        params.sortBy = sortBy;
      }

      // Remove null/undefined params
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await ticketService.getAllTickets(params);
      
      const responseData = response?.data;
      
      let ticketsList = [];
      let total = 0;
      let pages = 0;
      
      if (responseData?.success && responseData?.data) {
        const paginatedData = responseData.data;
        
        // Handle paginated response structure
        if (paginatedData.content) {
          ticketsList = paginatedData.content || [];
          total = paginatedData.totalElements || 0;
          pages = paginatedData.totalPages || 0;
        } else if (Array.isArray(paginatedData)) {
          ticketsList = paginatedData;
          total = paginatedData.length;
          pages = 1;
        }
      } else if (Array.isArray(responseData)) {
        ticketsList = responseData;
        total = responseData.length;
        pages = 1;
      }
      
      // Transform tickets to match expected format
      const transformedTickets = ticketsList.map(ticket => ({
        id: ticket.ticketId || ticket.id,
        ticketId: ticket.ticketId || ticket.id,
        ticketNumber: ticket.ticketNumber || ticket.ticketNumber,
        subject: ticket.subject || ticket.title || 'No Subject',
        title: ticket.subject || ticket.title || 'No Subject',
        description: ticket.description || '',
        status: (ticket.status || 'open').toLowerCase(),
        statusName: ticket.statusName || ticket.status,
        priority: (ticket.priority || 'medium').toLowerCase(),
        category: ticket.inquiryCategoryName || ticket.category || ticket.categoryName || 'Uncategorized',
        inquiryTypeName: ticket.inquiryTypeName || null,
        inquirySubCategoryName: ticket.inquirySubCategoryName || null,
        inquiryPriorityLevelName: ticket.inquiryPriorityLevelName || null,
        priorityName: ticket.inquiryPriorityLevelName || ticket.priorityName || ticket.priority,
        categoryId: ticket.categoryId,
        createdAt: ticket.createdAt,
        updatedAt: ticket.updatedAt,
        assignedTo: ticket.assignedAgent?.name || ticket.assignedTo || 'Unassigned',
        assignedAgent: ticket.assignedAgent,
        customer: ticket.customer || ticket.requester,
        lastActivity: ticket.updatedAt || ticket.createdAt,
        // Location fields
        countryName: ticket.countryName,
        countyName: ticket.countyName,
        subCountyName: ticket.subCountyName,
        wardName: ticket.wardName
      }));
      
      setTickets(transformedTickets);
      setPaginationData({
        totalElements: total,
        totalPages: pages,
        pageNumber: currentPage,
        pageSize: pageSize,
        first: currentPage === 0,
        last: currentPage >= pages - 1
      });
    } catch (error) {
      console.error('Error loading tickets:', error);
      setError(extractErrorMessage(error, 'Failed to load tickets'));
      toast.error(extractErrorMessage(error, 'Failed to load tickets'));
      setTickets([]);
      setPaginationData(null);
    } finally {
      setLoading(false);
    }
  };

  // Use tickets directly from API (no client-side filtering needed)
  const filteredTickets = tickets;

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setPriorityFilter('all');
    setSortBy('updated');
    setCurrentPage(0);
  };

  const handleViewTicket = (ticket) => {
    navigate(`/admin/tickets/${ticket.ticketId || ticket.id}`, { 
      state: { mode: 'view' } 
    });
  };

  const handleEditTicket = (ticket) => {
    navigate(`/admin/tickets/${ticket.ticketId || ticket.id}`, { 
      state: { mode: 'edit' } 
    });
  };

  const getPriorityColor = (priority) => {
    const priorityLower = (priority || 'medium').toLowerCase();
    switch (priorityLower) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getStatusColor = (status) => {
    const statusLower = (status || 'open').toLowerCase();
    switch (statusLower) {
      case 'open':
      case 'new':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'in-progress':
      case 'in progress':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'escalated':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && (!paginationData || newPage < paginationData.totalPages)) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="py-8">
        <div>
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">All Tickets</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              View and manage all tickets in the system
            </p>
          </div>

          {/* Filters Card */}
          {showFilters && (
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 flex-nowrap overflow-x-auto">
                  {/* Search Input */}
                  <div className="relative flex-1 min-w-0">
                    <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search tickets..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Status Dropdown */}
                  <div className="relative flex-shrink-0 min-w-[140px]">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
                    >
                      <option value="all">All Status</option>
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                      <option value="escalated">Escalated</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>

                  {/* Priority Dropdown */}
                  <div className="relative flex-shrink-0 min-w-[140px]">
                    <select
                      value={priorityFilter}
                      onChange={(e) => setPriorityFilter(e.target.value)}
                      className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
                    >
                      <option value="all">All Priority</option>
                      {priorities
                        .filter((priority) => priority.priorityId != null)
                        .map((priority) => {
                          const priorityCode = (priority.priorityCode || priority.priorityName || '').toLowerCase();
                          return (
                            <option key={priority.priorityId || priorityCode} value={priorityCode}>
                              {priority.priorityName || priority.priorityCode || priorityCode}
                            </option>
                          );
                        })}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>

                  {/* Sort By Dropdown */}
                  <div className="relative flex-shrink-0 min-w-[160px]">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="appearance-none px-4 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
                    >
                      <option value="updated">Last Updated</option>
                      <option value="created">Created Date</option>
                      <option value="priority">Priority</option>
                      <option value="due">Due Date</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleClearFilters}
                      className="text-sm px-4 py-2"
                    >
                      Clear All
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => setShowFilters(false)}
                      className="text-sm px-4 py-2"
                    >
                      Apply
                    </Button>
                    <button
                      onClick={() => setShowFilters(false)}
                      className="p-1.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search and Actions */}
          <div className="mb-6 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {loading ? 'Loading...' : `${paginationData?.totalElements || 0} tickets`}
              </span>
              <button 
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md transition-colors"
              >
                <Filter className="h-4 w-4" />
                <span>Filter</span>
              </button>
              <button 
                onClick={loadTickets}
                disabled={loading}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          )}

          {/* Tickets Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading tickets...</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Ticket className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100">No tickets found</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' 
                    ? 'Try adjusting your filters' 
                    : 'No tickets available'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800 rounded-lg">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="px-6 py-3 min-w-96 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Ticket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last Activity
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredTickets.map((ticket, index) => (
                    <tr 
                      key={ticket.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 text-center">
                        {currentPage * pageSize + index + 1}
                      </td>
                       <td className="px-6 py-4 min-w-96">
                         <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                           {ticket.title || ticket.subject || 'No Subject'}
                         </div>
                         <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                           {ticket.ticketNumber || ticket.id}
                         </div>
                         {ticket.description && (
                           <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 ">
                             {ticket.description}
                           </div>
                         )}
                       </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className={getStatusColor(ticket.status)}>
                          {ticket.statusName || ticket.status || 'open'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge className={getPriorityColor(ticket.priority)}>
                          {ticket.priorityName || ticket.priority || 'medium'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {ticket.category || 'Uncategorized'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {ticket.customer?.name || ticket.assignedTo || 'Unassigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDateTime(ticket.lastActivity || ticket.updatedAt || ticket.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewTicket(ticket);
                            }}
                            className="flex items-center space-x-1"
                          >
                            <Eye className="h-4 w-4" />
                            <span>View</span>
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditTicket(ticket);
                            }}
                            className="flex items-center space-x-1"
                          >
                            <Edit className="h-4 w-4" />
                            <span>Edit</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {paginationData && paginationData.totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing <span className="font-medium">{(paginationData.pageNumber * paginationData.pageSize) + 1}</span> to{' '}
                <span className="font-medium">{Math.min((paginationData.pageNumber + 1) * paginationData.pageSize, paginationData.totalElements)}</span> of{' '}
                <span className="font-medium">{paginationData.totalElements}</span> tickets
                {' '}(Page {paginationData.pageNumber + 1} of {paginationData.totalPages})
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={paginationData.first || loading}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={paginationData.last || loading}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminTicketsList;

